import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { leaveEnhancementAPI, leaveAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Clock, Plus, CheckCircle, XCircle, Calendar, DollarSign, AlertTriangle, Wallet } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

const LeaveEnhancements = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'verifier'].includes(user?.role);
  
  const [loading, setLoading] = useState(true);
  const [compOffBalance, setCompOffBalance] = useState(null);
  const [myCompOffRequests, setMyCompOffRequests] = useState([]);
  const [allCompOffRequests, setAllCompOffRequests] = useState([]);
  const [myEncashmentRequests, setMyEncashmentRequests] = useState([]);
  const [allEncashmentRequests, setAllEncashmentRequests] = useState([]);
  const [escalatedLeaves, setEscalatedLeaves] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  
  // Modals
  const [showCompOffModal, setShowCompOffModal] = useState(false);
  const [showEncashmentModal, setShowEncashmentModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionType, setActionType] = useState('');
  
  // Forms
  const [compOffForm, setCompOffForm] = useState({
    work_date: '',
    hours_worked: 8,
    reason: '',
    project_or_task: ''
  });
  
  const [encashmentForm, setEncashmentForm] = useState({
    leave_type: 'earned',
    days_to_encash: 1,
    remarks: ''
  });
  
  const [actionReason, setActionReason] = useState('');
  const [paymentRef, setPaymentRef] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const promises = [
        leaveEnhancementAPI.getMyCompOffRequests(),
        leaveEnhancementAPI.getMyCompOffBalance(),
        leaveEnhancementAPI.getMyEncashmentRequests(),
        leaveAPI.getLeaveBalances()
      ];
      
      if (isAdmin) {
        promises.push(
          leaveEnhancementAPI.getAllCompOffRequests(),
          leaveEnhancementAPI.getAllEncashmentRequests(),
          leaveEnhancementAPI.getEscalatedLeaves()
        );
      }
      
      const results = await Promise.all(promises);
      
      setMyCompOffRequests(results[0].data);
      setCompOffBalance(results[1].data);
      setMyEncashmentRequests(results[2].data);
      setLeaveBalances(results[3].data);
      
      if (isAdmin) {
        setAllCompOffRequests(results[4].data);
        setAllEncashmentRequests(results[5].data);
        setEscalatedLeaves(results[6].data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCompOff = async () => {
    try {
      if (!compOffForm.work_date || !compOffForm.reason) {
        toast.error('Please fill all required fields');
        return;
      }
      await leaveEnhancementAPI.requestCompOff(compOffForm);
      toast.success('Comp-off request submitted');
      setShowCompOffModal(false);
      setCompOffForm({ work_date: '', hours_worked: 8, reason: '', project_or_task: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleSubmitEncashment = async () => {
    try {
      if (encashmentForm.days_to_encash < 1) {
        toast.error('Please enter valid number of days');
        return;
      }
      await leaveEnhancementAPI.requestEncashment(encashmentForm);
      toast.success('Encashment request submitted');
      setShowEncashmentModal(false);
      setEncashmentForm({ leave_type: 'earned', days_to_encash: 1, remarks: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleAction = async () => {
    try {
      if (selectedItem.type === 'compoff') {
        await leaveEnhancementAPI.processCompOff(selectedItem.id, actionType, actionReason);
      } else if (selectedItem.type === 'encashment') {
        await leaveEnhancementAPI.processEncashment(selectedItem.id, actionType, paymentRef, actionReason);
      }
      toast.success(`Request ${actionType}d successfully`);
      setShowActionModal(false);
      setSelectedItem(null);
      setActionReason('');
      setPaymentRef('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleCheckEscalations = async () => {
    try {
      const res = await leaveEnhancementAPI.checkEscalations();
      toast.success(`Escalation check complete: ${res.data.escalated_count} leaves escalated`);
      fetchData();
    } catch (error) {
      toast.error('Failed to check escalations');
    }
  };

  const openActionModal = (item, type, action) => {
    setSelectedItem({ ...item, type });
    setActionType(action);
    setShowActionModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      used: 'bg-blue-100 text-blue-700',
      expired: 'bg-gray-100 text-gray-700',
      processed: 'bg-green-100 text-green-800',
      pending_escalated: 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="leave-enhancements-page">
      <Header 
        title="Leave Enhancements" 
        subtitle="Comp-off, encashment, and escalation management"
      />
      
      <div className="p-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="dashboard-card" data-testid="compoff-balance-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Comp-Off Balance</p>
                <p className="text-3xl font-heading font-semibold text-blue-600">
                  {compOffBalance?.available || compOffBalance?.available_days || 0}
                </p>
                <p className="text-sm text-slate-500">days available</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-100">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
          
          {leaveBalances.map(bal => (
            <div key={bal.leave_type} className="dashboard-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{bal.leave_type} Leave</p>
                  <p className="text-3xl font-heading font-semibold text-slate-900">{bal.balance || 0}</p>
                  <p className="text-sm text-slate-500">days remaining</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-100">
                  <Calendar className="w-5 h-5 text-slate-600" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8">
          <Button onClick={() => setShowCompOffModal(true)} data-testid="request-compoff-btn">
            <Plus className="w-4 h-4 mr-2" />
            Request Comp-Off
          </Button>
          <Button variant="outline" onClick={() => setShowEncashmentModal(true)} data-testid="request-encashment-btn">
            <Wallet className="w-4 h-4 mr-2" />
            Encash Leave
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={handleCheckEscalations} data-testid="check-escalations-btn">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Check Escalations
            </Button>
          )}
        </div>

        <Tabs defaultValue="my-compoff" className="space-y-6">
          <TabsList>
            <TabsTrigger value="my-compoff">My Comp-Off</TabsTrigger>
            <TabsTrigger value="my-encashment">My Encashment</TabsTrigger>
            {isAdmin && <TabsTrigger value="all-compoff">All Comp-Off</TabsTrigger>}
            {isAdmin && <TabsTrigger value="all-encashment">All Encashment</TabsTrigger>}
            {isAdmin && <TabsTrigger value="escalated">Escalated Leaves</TabsTrigger>}
          </TabsList>

          {/* My Comp-Off Requests */}
          <TabsContent value="my-compoff">
            <div className="dashboard-card" data-testid="my-compoff-list">
              <h3 className="font-heading text-lg font-semibold mb-4">My Comp-Off Requests</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Work Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Hours</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Days Earned</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Reason</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Expiry</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myCompOffRequests.map((req) => (
                      <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium">{formatDate(req.work_date)}</td>
                        <td className="py-3 px-4">{req.hours_worked}h</td>
                        <td className="py-3 px-4 text-blue-600 font-semibold">{req.days_earned}</td>
                        <td className="py-3 px-4 max-w-xs truncate">{req.reason}</td>
                        <td className="py-3 px-4 text-slate-500">{formatDate(req.expiry_date)}</td>
                        <td className="py-3 px-4">{getStatusBadge(req.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {myCompOffRequests.length === 0 && (
                  <p className="text-center py-8 text-slate-400">No comp-off requests</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* My Encashment Requests */}
          <TabsContent value="my-encashment">
            <div className="dashboard-card" data-testid="my-encashment-list">
              <h3 className="font-heading text-lg font-semibold mb-4">My Encashment Requests</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Leave Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Days</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Per Day</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Total Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">FY</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myEncashmentRequests.map((req) => (
                      <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 capitalize font-medium">{req.leave_type}</td>
                        <td className="py-3 px-4">{req.days_to_encash}</td>
                        <td className="py-3 px-4">{formatCurrency(req.per_day_amount)}</td>
                        <td className="py-3 px-4 text-emerald-600 font-semibold">{formatCurrency(req.total_amount)}</td>
                        <td className="py-3 px-4">{req.financial_year}</td>
                        <td className="py-3 px-4">{getStatusBadge(req.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {myEncashmentRequests.length === 0 && (
                  <p className="text-center py-8 text-slate-400">No encashment requests</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Admin: All Comp-Off */}
          {isAdmin && (
            <TabsContent value="all-compoff">
              <div className="dashboard-card" data-testid="all-compoff-list">
                <h3 className="font-heading text-lg font-semibold mb-4">All Comp-Off Requests</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Work Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Hours</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Days</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Reason</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allCompOffRequests.map((req) => (
                        <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{req.employee_name}</p>
                            <p className="text-xs text-slate-400">{req.employee_code}</p>
                          </td>
                          <td className="py-3 px-4">{formatDate(req.work_date)}</td>
                          <td className="py-3 px-4">{req.hours_worked}h</td>
                          <td className="py-3 px-4 text-blue-600 font-semibold">{req.days_earned}</td>
                          <td className="py-3 px-4 max-w-xs truncate">{req.reason}</td>
                          <td className="py-3 px-4">{getStatusBadge(req.status)}</td>
                          <td className="py-3 px-4">
                            {req.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => openActionModal(req, 'compoff', 'approve')}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => openActionModal(req, 'compoff', 'reject')}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {allCompOffRequests.length === 0 && (
                    <p className="text-center py-8 text-slate-400">No comp-off requests</p>
                  )}
                </div>
              </div>
            </TabsContent>
          )}

          {/* Admin: All Encashment */}
          {isAdmin && (
            <TabsContent value="all-encashment">
              <div className="dashboard-card" data-testid="all-encashment-list">
                <h3 className="font-heading text-lg font-semibold mb-4">All Encashment Requests</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Leave Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Days</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allEncashmentRequests.map((req) => (
                        <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{req.employee_name}</p>
                            <p className="text-xs text-slate-400">{req.employee_code}</p>
                          </td>
                          <td className="py-3 px-4 capitalize">{req.leave_type}</td>
                          <td className="py-3 px-4">{req.days_to_encash}</td>
                          <td className="py-3 px-4 text-emerald-600 font-semibold">{formatCurrency(req.total_amount)}</td>
                          <td className="py-3 px-4">{getStatusBadge(req.status)}</td>
                          <td className="py-3 px-4">
                            {req.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => openActionModal(req, 'encashment', 'approve')}>
                                  Approve
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => openActionModal(req, 'encashment', 'reject')}>
                                  Reject
                                </Button>
                              </div>
                            )}
                            {req.status === 'approved' && (
                              <Button size="sm" variant="outline" className="text-green-600" onClick={() => openActionModal(req, 'encashment', 'process')}>
                                <DollarSign className="w-4 h-4 mr-1" />
                                Process
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {allEncashmentRequests.length === 0 && (
                    <p className="text-center py-8 text-slate-400">No encashment requests</p>
                  )}
                </div>
              </div>
            </TabsContent>
          )}

          {/* Admin: Escalated Leaves */}
          {isAdmin && (
            <TabsContent value="escalated">
              <div className="dashboard-card" data-testid="escalated-leaves-list">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-semibold">Escalated Leave Applications</h3>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                    {escalatedLeaves.length} pending
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Leave Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Dates</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Days</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Pending Since</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {escalatedLeaves.map((leave) => (
                        <tr key={leave.id} className="border-b border-slate-100 hover:bg-slate-50 bg-orange-50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{leave.employee_name}</p>
                            <p className="text-xs text-slate-400">{leave.employee_code}</p>
                          </td>
                          <td className="py-3 px-4 capitalize">{leave.leave_type}</td>
                          <td className="py-3 px-4">
                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </td>
                          <td className="py-3 px-4">{leave.days}</td>
                          <td className="py-3 px-4 text-orange-600">
                            {leave.days_pending_before_escalation || '3+'} days
                          </td>
                          <td className="py-3 px-4">{getStatusBadge(leave.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {escalatedLeaves.length === 0 && (
                    <p className="text-center py-8 text-slate-400">No escalated leaves</p>
                  )}
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Comp-Off Request Modal */}
        <Dialog open={showCompOffModal} onOpenChange={setShowCompOffModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Comp-Off</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Work Date (Holiday/Weekend) *</Label>
                <Input
                  type="date"
                  value={compOffForm.work_date}
                  onChange={(e) => setCompOffForm({...compOffForm, work_date: e.target.value})}
                  data-testid="compoff-date"
                />
              </div>
              <div>
                <Label>Hours Worked *</Label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={compOffForm.hours_worked}
                  onChange={(e) => setCompOffForm({...compOffForm, hours_worked: parseFloat(e.target.value)})}
                  data-testid="compoff-hours"
                />
                <p className="text-xs text-slate-500 mt-1">8+ hours = 1 day, 4-8 hours = 0.5 day</p>
              </div>
              <div>
                <Label>Reason *</Label>
                <Textarea
                  value={compOffForm.reason}
                  onChange={(e) => setCompOffForm({...compOffForm, reason: e.target.value})}
                  placeholder="Why did you work on this day?"
                  data-testid="compoff-reason"
                />
              </div>
              <div>
                <Label>Project/Task</Label>
                <Input
                  value={compOffForm.project_or_task}
                  onChange={(e) => setCompOffForm({...compOffForm, project_or_task: e.target.value})}
                  placeholder="Optional"
                  data-testid="compoff-project"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompOffModal(false)}>Cancel</Button>
              <Button onClick={handleSubmitCompOff} data-testid="compoff-submit">Submit Request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Encashment Request Modal */}
        <Dialog open={showEncashmentModal} onOpenChange={setShowEncashmentModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Leave Encashment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Leave Type</Label>
                <Select value={encashmentForm.leave_type} onValueChange={(v) => setEncashmentForm({...encashmentForm, leave_type: v})}>
                  <SelectTrigger data-testid="encashment-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="earned">Earned Leave</SelectItem>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Days to Encash *</Label>
                <Input
                  type="number"
                  min="1"
                  value={encashmentForm.days_to_encash}
                  onChange={(e) => setEncashmentForm({...encashmentForm, days_to_encash: parseInt(e.target.value) || 0})}
                  data-testid="encashment-days"
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={encashmentForm.remarks}
                  onChange={(e) => setEncashmentForm({...encashmentForm, remarks: e.target.value})}
                  placeholder="Optional"
                  data-testid="encashment-remarks"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEncashmentModal(false)}>Cancel</Button>
              <Button onClick={handleSubmitEncashment} data-testid="encashment-submit">Submit Request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action Modal */}
        <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'approve' && 'Approve Request'}
                {actionType === 'reject' && 'Reject Request'}
                {actionType === 'process' && 'Process Payment'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedItem && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedItem.employee_name}</p>
                  <p className="text-sm text-slate-500">
                    {selectedItem.type === 'compoff' 
                      ? `${selectedItem.days_earned} day(s) comp-off`
                      : formatCurrency(selectedItem.total_amount)}
                  </p>
                </div>
              )}
              
              {actionType === 'reject' && (
                <div>
                  <Label>Rejection Reason</Label>
                  <Textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Reason for rejection..."
                    data-testid="action-reason"
                  />
                </div>
              )}
              
              {actionType === 'process' && (
                <div>
                  <Label>Payment Reference</Label>
                  <Input
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder="Transaction ID or reference"
                    data-testid="payment-ref"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActionModal(false)}>Cancel</Button>
              <Button 
                onClick={handleAction}
                className={actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}
                data-testid="action-confirm"
              >
                {actionType === 'approve' && 'Approve'}
                {actionType === 'reject' && 'Reject'}
                {actionType === 'process' && 'Process Payment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LeaveEnhancements;
